import {coerceBooleanProperty} from '@angular/cdk/coercion';
import {DOCUMENT} from '@angular/common';
import {
  AfterViewInit, ChangeDetectionStrategy,
  ChangeDetectorRef, Component,
  Directive, ElementRef, HostBinding,
  Inject, Injector, Input, NgZone,
  OnChanges, OnDestroy, Optional, PLATFORM_ID,
  Renderer2, Self
} from '@angular/core';
import {ControlValueAccessor, FormGroupDirective, NgControl, NgForm, Validator} from '@angular/forms';
import {CanDisable, CanUpdateErrorState, ErrorStateMatcher, mixinDisabled, mixinErrorState} from '@angular/material/core';
import {MatFormFieldControl} from '@angular/material/form-field';
import {DomSanitizer} from '@angular/platform-browser';
import {QuillEditorBase, QuillService} from 'ngx-quill';
import Quill from 'quill';
import {Subject, Subscription} from 'rxjs';
import {debounceTime, distinctUntilChanged} from 'rxjs/operators';

const Link = Quill.import('formats/link');
// Override the existing property on the Quill global object and add custom protocols
Link.PROTOCOL_WHITELIST = ['http', 'https', 'mailto', 'tel', 'sms'];

class CustomLinkSanitizer extends Link {
  static sanitize(url) {
    // Run default sanitize method from Quill
    const sanitizedUrl = super.sanitize(url);

    // Not whitelisted URL based on protocol so, let's return `blank`
    if (!sanitizedUrl || sanitizedUrl === 'about:blank') {
      return sanitizedUrl;
    }

    // Verify if the URL already have a whitelisted protocol
    const hasWhitelistedProtocol = (this as any).PROTOCOL_WHITELIST.some(function(protocol) {
      return sanitizedUrl.startsWith(protocol);
    });

    if (hasWhitelistedProtocol) {
      return sanitizedUrl;
    }

    // if not, then append only 'http' to not to be a relative URL
    return `http://${sanitizedUrl}`;
  }
}

Quill.register(CustomLinkSanitizer, true);
class MatQuillBase extends QuillEditorBase {
  stateChanges = new Subject<void>();
  bounds = 'self';

  constructor(
    public _defaultErrorStateMatcher: ErrorStateMatcher,
    public _parentForm: NgForm,
    public _parentFormGroup: FormGroupDirective,
    public ngControl: NgControl,
    injector: Injector,
    cdr: ChangeDetectorRef,
    elementRef: ElementRef,
    domSanitizer: DomSanitizer,
    platformId: any,
    renderer: Renderer2,
    zone: NgZone,
    service: QuillService
  ) {
    super(injector, elementRef, cdr, domSanitizer, platformId, renderer, zone, service);
  }
}

const _MatQuillMixinBase = mixinErrorState(mixinDisabled(MatQuillBase));

@Directive()
export abstract class _MatQuillBase
  extends _MatQuillMixinBase
  implements
    AfterViewInit,
    CanDisable,
    CanUpdateErrorState,
    ControlValueAccessor,
    MatFormFieldControl<any>,
    OnChanges,
    OnDestroy,
    Validator
{
  abstract controlType: string;
  focused = false;
  abstract id: string;
  private contentChangedSubscription: Subscription;
  private blurSubscription: Subscription;
  private focusSubscription: Subscription;

  constructor(
    defaultErrorStateMatcher: ErrorStateMatcher,
    @Optional() parentForm: NgForm,
    @Optional() parentFormGroup: FormGroupDirective,
    @Optional() @Self() public ngControl: NgControl,
    cdr: ChangeDetectorRef,
    elementRef: ElementRef,
    domSanitizer: DomSanitizer,
    @Inject(PLATFORM_ID) platformId: any,
    renderer: Renderer2,
    zone: NgZone,
    injector: Injector,
    service: QuillService,
    @Inject(DOCUMENT) doc: any
  ) {
    super(
      defaultErrorStateMatcher,
      parentForm,
      parentFormGroup,
      ngControl,
      injector,
      cdr,
      elementRef,
      domSanitizer,
      platformId,
      renderer,
      zone,
      service
    );

    if (this.ngControl) {
      this.ngControl.valueAccessor = this;
    }

    this.contentChangedSubscription = this.onContentChanged.pipe(debounceTime(300), distinctUntilChanged()).subscribe(() => {
      this.updateErrorState();
      this.stateChanges.next();
    });

    this.blurSubscription = this.onBlur.subscribe(() => {
      this.focused = false;
      if (!!this.ngControl && !this.ngControl.control.touched) {
        this.ngControl.control.markAsTouched();
        this.updateErrorState();
      }
      this.stateChanges.next();
    });

    this.focusSubscription = this.onFocus.subscribe(() => {
      this.focused = true;
      this.stateChanges.next();
    });
  }

  ngOnDestroy() {
    this.contentChangedSubscription.unsubscribe();
    this.blurSubscription.unsubscribe();
    this.focusSubscription.unsubscribe();
    super.ngOnDestroy();
  }

  /*
   * GETTERS & SETTERS
   */

  @Input()
  disabled = false;

  get empty() {
    return !coerceBooleanProperty(this.value);
  }

  @Input()
  placeholder = '';

  @Input()
  required = false;

  @HostBinding('class.floating')
  get shouldLabelFloat() {
    return this.focused || !this.empty;
  }

  get value(): any {
    try {
      return this.valueGetter(this.quillEditor, this.editorElem!);
    } catch (e) {
      return;
    }
  }
  set value(value: any) {
    this.writeValue(value);
    this.stateChanges.next();
  }

  /*
   * METHODS
   */

  blur(): void {
    (this.editorElem.childNodes as NodeListOf<HTMLElement>)[0].blur();
  }

  focus(): void {
    this.quillEditor.focus();
  }

  @HostBinding('attr.aria-describedby') _describedBy = '';
  setDescribedByIds(ids: string[]): void {
    this._describedBy = ids.join(' ');
  }

  onContainerClick(event: MouseEvent): void {
    if ((event.target as HTMLElement)?.closest('.ql-tooltip')) {
      return;
    }
    if (!this.focused) {
      this.quillEditor.focus();
    }
  }

  static ngAcceptInputType_disabled: boolean | string | null | undefined;
  static ngAcceptInputType_required: boolean | string | null | undefined;
}
// Increasing integer for generating unique ids for mat-quill components.
let nextUniqueId = 0;

@Component({
  selector: 'app-quill',
  exportAs: 'appQuill',
  template: `
    <ng-container *ngIf="toolbarPosition !== 'top'">
      <div quill-editor-element *ngIf="!preserve"></div>
      <pre quill-editor-element *ngIf="preserve"></pre>
    </ng-container>
    <ng-content select="[quill-editor-toolbar]"></ng-content>
    <ng-container *ngIf="toolbarPosition === 'top'">
      <div quill-editor-element *ngIf="!preserve"></div>
      <pre quill-editor-element *ngIf="preserve"></pre>
    </ng-container>
    <ng-content></ng-content>
  `,
  styleUrls: ['./quill.component.scss'],
  providers: [{provide: MatFormFieldControl, useExisting: QuillInputComponent}],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class QuillInputComponent extends _MatQuillBase implements AfterViewInit {
  private _testId: string;
  @Input() set testId(testId: string) {
    if (this.elementRef) {
      this.renderer.setAttribute(this.elementRef.nativeElement, 'data-test-id', testId);
    }
    this._testId = testId;
  }
  get testId(): string {
    return this._testId;
  }
  @Input() disabled = false;
  controlType = 'app-quill';
  @HostBinding() id = `${'app-quill'}-${nextUniqueId++}`;
  @Input() errorStateMatcher: ErrorStateMatcher;
  @Input() styles: any = null;

  static ngAcceptInputType_disabled: boolean | string | null | undefined;
  static ngAcceptInputType_required: boolean | string | null | undefined;

  ngAfterViewInit(): void {
    super.ngAfterViewInit();
    this.renderer.setAttribute(
      this.elementRef.nativeElement.querySelector('[quill-editor-element]'),
      'data-test-id',
      [this.testId, 'input'].join('_')
    );
  }
}
